import React from "react";

const policyText = `Effective Date: Apr 27th 2026
 
 
This Privacy Policy applies to the Ceviliyar Platform, operated by Rizz Software Solutions Private Limited.
 
Registered Address:
Level 8 & 9, Olympia Teknos,
No. 28, SIDCO Industrial Estate,
Guindy, Chennai – 600032, Tamil Nadu, India
 
Contact: support@rizzsoftware.com | ceviliyar.in

1. Introduction

This Privacy Policy explains how Rizz Software Solutions Private Limited (referred to herein as
the “Company,” “we,” “our,” or “us”), operating the Ceviliyar digital healthcare platform
(“Platform”), collects, uses, stores, shares, and protects personal data. This Policy applies to all
users of the Platform, including Partner Organisations (hospitals, clinics, and agencies),
Healthcare Professionals (HCPs), Patients, and Family Members, whether located in India or
abroad. By accessing or using the Platform, you acknowledge that you have read, understood,
and agree to be bound by this Privacy Policy. If you do not agree to this Policy, you must not
access or use the Platform.

2. Definitions

   ●   “Personal Data” means any data about an individual who is identifiable by or in relation
       to such data, as defined under the Digital Personal Data Protection Act, 2023 (“DPDP
       Act”).

   ●   “Sensitive Personal Data” means health data, biometric data, financial data, and any
       other category of data classified as sensitive under applicable law.

   ●   “Data Fiduciary” means Rizz Software Solutions Private Limited, which determines the
       purpose and means of processing Personal Data through the Platform.

   ●   “Data Principal” means the individual to whom the Personal Data relates, including
       Patients, Family Members, Healthcare Professionals, and Partner representatives.

   ●   “Data Processor” means any third-party entity that processes data on behalf of the
       Data Fiduciary, including payment gateways and cloud hosting providers.

   ●   “HCP” means a Healthcare Professional, whether employed by or affiliated with a
       Partner Organisation.

3. Personal Data We Collect

The following sections describe the categories of personal data we collect through the
Platform, including the Partner Portal, mobile applications, and website.

3.1 Partner Organisation Data

When a hospital, clinic, agency, or individual contractor registers on the Platform, we collect the
following:


3.1.1 Basic Information

Data Field                             Purpose                                Legal Basis
Partner Name                           Platform listing and                   Contractual necessity
                                       identification
Slogan                                 Branding on Platform profile           Consent
Number of Patients                     Operational planning and               Legitimate interest
                                       capacity assessment
Cover Picture / Logo                   Visual identification on the           Consent
                                       Platform



3.1.2 Address Details

Data Field                                                 Purpose
GPS Coordinates                                            Proximity-based discovery and service radius
                                                           mapping
Address Line 1 & 2, City, State, Pin Code                  Operational verification and invoicing
Service Radius                                             Determining coverage area for patient
                                                           matching



3.1.3 Contact Details (Primary & Secondary)

Data Field                                                 Purpose
Title, Name                                                Identification and formal correspondence
Phone Number                                               Operational communication, booking alerts,
                                                           and OTP verification
Email                                                      Account management, notifications, invoices,
                                                           and reports



3.1.4 Financial Information

Important: Bank details (IFSC code, bank account name, and bank account number) are
collected solely for the purpose of facilitating payment settlements through our payment
gateway partner, Razorpay. We do not store bank account details on our own databases. These
details are transmitted directly to Razorpay using end-to-end encryption and are subject to
Razorpay’s own privacy and security policies, as well as RBI guidelines for payment data
storage.


3.1.5 Regulatory Documents

Data Field                                                 Purpose
PAN Card Number                                            Tax compliance and TDS deduction under
                                                           Income Tax Act
GST Certificate Number                                     GST invoicing and regulatory compliance
Authorised Representative Name                             Legal accountability and agreement execution
Company Registration Certificate                           Verification of legal entity status



3.1.6 Services & Commission Configuration

   ●   Services availability (Doctor visits, Physiotherapy, Lab, Pharmacy, Equipment rental,
       Nursing care, Nurse, Nursing Assistant)

   ●   Commission percentage and frequency

   ●   Service names, individual service details, shift types, and service fees


3.1.7 Branch Details For multi-branch partners, we collect branch name, branch contact
number, and primary and secondary contact persons (title, full name, contact number, and
email ID) for each branch.

3.2 Patient Data

Patient data is collected through two channels:

   1.  Direct Registration: Voluntarily provided by the Patient or an authorized Family Member
       directly through the Ceviliyar mobile application.
   2.  Partner Onboarding: Entered securely by an authorized Partner Organisation (such as a
       hospital, clinic, or agency) through the Partner Portal, pursuant to the delivery of home
       healthcare services.

Regardless of the channel through which data is collected, the following personal data is
processed:

Data Field                             Purpose                                 Legal Basis
Title & Patient Name                   Patient identification and              Consent / Contractual
                                       clinical records                        necessity
Gender                                 Clinical relevance and care             Consent / Contractual
                                       personalisation                         necessity
Date of Birth or Age                   Age verification and clinical           Consent / Contractual
                                       care protocols                          necessity
Address                                Home visit scheduling,                  Consent / Contractual
                                       GPS-based service delivery              necessity
ABHA Number (upon ABDM                 Future integration with                 Legal obligation (when
integration)                           Ayushman Bharat Digital Health applicable)
                                       Mission
MRN (Medical Record Number) Continuity of care and                             Contractual necessity
                                       cross-referencing with hospital
                                       records
Emergency Contact Number               Critical care communication             Vital interest
                                       and SOS functionality
Discharge Summary                      Clinical handover and                   Contractual necessity
                                       continuity of home care
Phone Number                           Booking confirmations, care             Consent / Contractual
                                       updates, and OTP verification           necessity

   ●   Note on Date of Birth / Age: The Platform collects either the patient’s date of birth or
       their age (in years), depending on data availability. In cases where a patient’s exact date
       of birth is not known or not recorded in their medical history, the Platform accepts the
       patient’s age as an alternative. This approach accommodates clinical realities across
       India, where certain patient demographics may not have a formally documented date of
       birth. Age data is used for clinical care protocols, treatment planning, and medication
       dosage considerations.

   ●   Note on ABHA Number: The ABHA (Ayushman Bharat Health Account) number is not
       currently collected as a mandatory field. Collection of ABHA numbers will commence
       upon the Platform’s integration with the Ayushman Bharat Digital Health Mission (ABDM)
       ecosystem.  Until such integration is activated, this field remains optional. Users will be
       notified when ABDM integration becomes available, and consent for ABHA-linked data
       sharing will be obtained at that time.

   ●   Note on Legal Basis: Where the legal basis is listed as “Consent / Contractual necessity,”
       the applicable basis depends on the data collection channel. When a patient registers

directly through the Ceviliyar mobile application, the legal basis is the patient’s explicit
       consent provided during registration. When patient data is entered by a Partner
       Organisation through the Partner Portal, the legal basis is contractual necessity under
       the Service Partnership Agreement, and the Partner is responsible for ensuring that
       appropriate patient consent has been obtained prior to data entry.

3.3 Healthcare Professional (HCP) Data

HCPs are onboarded through the Partner Portal. The following personal data is collected:

Data Field                              Purpose                                 Legal Basis
Title & Name (as in Aadhaar)            Identity verification and               Legal obligation
                                        Aadhaar-linked authentication
Date of Birth                           Age verification and credential         Contractual necessity
                                        validation
Role                                    Assignment of appropriate               Contractual necessity
                                        clinical duties
Contact Number                          Shift notifications, duty alerts,       Contractual necessity
                                        and emergency
                                        communication
Email ID                                Account access, training                Contractual necessity
                                        materials, and reports


3.4 Data Collected Automatically

In addition to the data provided directly by users, the Platform automatically collects:

   ●   GPS location data during active duty shifts (Start Duty / End Duty logs) for attendance
       verification

   ●   Device information (model, operating system, app version) for technical support

   ●   IP address and session logs for security monitoring and fraud prevention

   ●   Voice recordings processed through our Voice-First AI for clinical log generation
       (Tanglish voice commands)

   ●   Push notification tokens for real-time alerts to patients and families

   ●   Clinical logs including vitals, observations, medication records, and care notes

4. How We Use Your Personal Data

We use the personal data collected for the following purposes:

   ●   Service Delivery: To facilitate home healthcare bookings, scheduling, patient-HCP
       matching, and real-time care coordination.

   ●   Care Verification & Transparency: GPS-validated check-ins, digital clinical logs, and
       verified service reports accessible to families.

   ●   Payment Processing: Automated billing, receipt generation, commission deduction, and
       settlement through Razorpay.

   ●   Identity Verification: Aadhaar-based identity verification for HCPs and credential
       validation for clinical professionals.

   ●   Quality Assurance: Monitoring service quality through ratings, reviews, and clinical audit
       trails.

   ●   Regulatory Compliance: Maintaining records as required under NABH accreditation
       standards, the DPDP Act 2023, and applicable medical record-keeping regulations.

   ●   Communication: Sending booking confirmations, shift alerts, care updates, and
       administrative notifications.

   ●   Platform Improvement: Analysing aggregated and anonymised usage data to improve
       features and user experience.

   ●   Fraud Prevention: Detecting and preventing ghost attendance, off-platform solicitation,
       and unauthorised access.

5. Legal Basis for Processing

We process personal data based on the following legal grounds under the DPDP Act, 2023 and
applicable laws:

   ●   Consent: Where you have provided explicit, informed consent for specific processing
       activities.

   ●   Contractual Necessity: Where processing is necessary to perform our obligations
       under the Service Partnership Agreement or user terms.

   ●   Legal Obligation: Where processing is required to comply with Indian laws, including the

Information Technology Act 2000, medical record-keeping requirements, and tax
       regulations.

   ●   Vital Interest: Where processing is necessary to protect the life or health of a patient
       (e.g., emergency contact usage, SOS functionality).

   ●   Legitimate Interest: Where processing is necessary for our legitimate business
       interests, provided these do not override your fundamental rights.

6. Data Sharing & Third Parties

We do not sell personal data. We share data only in the following circumstances:

   ●   Partner Organisations: Patient and HCP data relevant to care delivery is shared with the
       associated Partner for clinical and operational management.

   ●   Payment Gateway (Razorpay): Financial data is transmitted to Razorpay for payment
       processing. Razorpay is PCI-DSS compliant and processes data in accordance with RBI
       guidelines.

   ●   Cloud Infrastructure: Data is hosted on secure, India-based cloud servers to ensure
       compliance with data localisation requirements under the DPDP Act.

   ●   Family Members: Care transparency data (GPS logs, clinical reports, vitals) is shared
       with authorised family members through the Family Transparency App, with patient or
       authorised caregiver consent.

   ●   Legal Authorities: We may disclose data when required by law, court order, or
       regulatory authority.

   ●   ABDM / ABHA Integration (Planned): The Platform is being designed for future
       integration with the Ayushman Bharat Digital Health Mission (ABDM) ecosystem. Upon
       activation of this integration, health data may be shared through the ABDM network in
       accordance with applicable protocols. This feature is not currently active, and users will
       be notified and consent obtained prior to any ABHA-linked data sharing.

7. Data Retention

We retain personal data as follows:

   ●   Active Account Data: Retained for as long as the user account remains active on the
       Platform.

●   Clinical Records: Retained as required under applicable medical record-keeping
       regulations, even after account deletion.

   ●   Transaction Data: Retained for a minimum of 8 years as required under Indian tax and
       financial regulations.

   ●   Audit Trails (GPS logs, duty logs, timestamps): Retained for a minimum of 3 years
       post-termination for regulatory compliance and dispute resolution.

   ●   Consent Records: Retained for the duration of the processing activity and for a
       reasonable period thereafter to demonstrate compliance.

   ●   Voice Recordings: Processed in real-time for clinical log generation; raw voice data is
       not retained beyond the processing period unless required for quality audit purposes.

8. Account Deletion & Data Erasure

Users have the right to delete their accounts and request the erasure of their personal data. You may initiate this process through either of the following methods:

In-App Deletion (Recommended): You can easily request account deletion directly within the Ceviliyar mobile application by navigating to your Profile/Settings menu and selecting "Delete Account."

Email Request: Alternatively, if you no longer have access to the app, you may request account deletion by contacting us at support@rizzsoftware.com from your registered email address.

Upon receipt of a valid deletion request through the app:

Immediate Account Deletion: Your account access and associated profile will be permanently deleted immediately.

Data Purging: Personal identifiers not subject to legal retention requirements will be purged or anonymised concurrent with your account deletion.

Mandatory Legal Retention: Clinical data, audit trails, billing records, and consent records will be retained strictly as required by Indian law and applicable medical regulations, with personal identifiers anonymised to the extent legally permitted.

Healthcare Professional Accounts: Records related to patient care (service logs, clinical notes, attendance records) will be retained even after HCP account deletion, as required by medical record-keeping regulations. The associated Partner Organisation will be notified of the account closure.

9. Cross-Jurisdictional Compliance

9.1 Current Operational Jurisdictions

The Platform currently serves users in the following jurisdictions:

   ●   Republic of India
   ●
9.2 Applicable Data Protection Laws

We currently comply with the following data protection regulations:

   ●   The Digital Personal Data Protection Act, 2023 (DPDP Act)
   ●   The Information Technology Act, 2000, and applicable rules thereunder
   ●   Medical record-keeping guidelines as stipulated by the National Medical Commission
       (NMC) and the Clinical Establishments (Registration and Regulation) Act, 2010
   ●
9.3 Planned Expansion Jurisdictions

The Platform intends to expand its Family Transparency services to NRI families residing in the
following jurisdictions, whose family members in India require home healthcare:

   ●   Canada

   ●   Singapore

   ●   United Arab Emirates (UAE)

   ●   Saudi Arabia

   ●   Australia

   ●   Qatar

   ●   Other jurisdictions with significant Indian diaspora populations

Upon expansion into these jurisdictions, the Company will ensure compliance with the
applicable local data protection laws, including but not limited to the Personal Data Protection
Act (PDPA) in Singapore, the Privacy Act 1988 in Australia, PIPEDA in Canada, and equivalent
regulations in the UAE, Saudi Arabia, and Qatar. Users in these jurisdictions will be notified of
any jurisdiction-specific rights and obligations prior to or upon service activation in their region.

9.4 Conflict of Laws

Where a conflict exists between deletion rights under a user’s local law and clinical record
retention obligations under Indian law, the clinical retention obligation shall prevail for
healthcare data, as the Platform processes health data in India under Indian medical

record-keeping regulations. The local deletion right shall be honoured for non-clinical personal
data (such as account preferences, communication preferences, and non-health-related
profile information).

10. Data Security Measures

We implement the following technical and organisational measures to protect personal data:

   ●   Regional Data Sovereignty: All patient and provider data is hosted on India-based
       secure cloud instances, ensuring data localisation compliance.

   ●   Encryption: Data in transit is protected using TLS 1.2 or higher. Sensitive data at rest is
       encrypted using industry-standard AES-256 encryption.

   ●   Role-Based Access Control (RBAC): Access to patient data is governed by the principle
       of least privilege. HCPs can only access records of their assigned patients during active
       shifts.

   ●   Aadhaar-Linked Verification: Identity verification ensures person-to-profile matching
       for all HCPs.

   ●   GPS-Verified Logs: Tamper-resistant attendance and location verification through
       real-time GPS validation.

   ●   Payment Security: All financial transactions are processed through PCI-DSS compliant
       infrastructure (Razorpay). Bank details are not stored on our systems.

10.1 Data Breach Notification

In the unlikely event of a personal data breach, we will promptly notify the Data Protection
Board of India and the affected Data Principals in accordance with the mandatory timelines,
procedures, and formats stipulated by the Digital Personal Data Protection Act, 2023.
11. Your Rights

Under the DPDP Act, 2023 and applicable laws, you have the following rights:

   ●   Right to Access: You may request a summary of your personal data being processed.

   ●   Right to Correction: You may request correction of inaccurate or incomplete personal
       data.

   ●   Right to Erasure: You may request deletion of your personal data, subject to legal
       retention requirements.

●   Right to Withdraw Consent: You have the right to withdraw your consent for specific
       data processing activities (e.g., marketing communications, background location) at any
       time through the app settings or by contacting us. Withdrawal of consent will not affect
       the legality of processing based on consent before its withdrawal, but may impact our
       ability to provide certain features or services.
   ●   Right to Nominate: You may nominate an individual to exercise your data rights in the
       event of your death or incapacity.

   ●   Right to Grievance Redressal: You may file a complaint regarding data processing with
       our Grievance Officer or the Data Protection Board of India.

To exercise any of these rights, contact our Grievance Officer at: support@rizzsoftware.com

12. Cookies & Tracking Technologies

The Platform may use cookies and similar technologies to maintain session state, remember
preferences, and analyse Platform usage.  We do not use third-party advertising trackers. Users
can manage cookie preferences through their browser settings.

13. Children’s Privacy

The Platform is not designed for use by children under the age of 18. Patient onboarding for
minors is performed either by the authorised Partner Organisation through the Partner Portal,
or by a parent or legal guardian through the Ceviliyar mobile application. We do not knowingly
collect personal data from children without verifiable parental or guardian consent.

14. Changes to This Policy

We may update this Privacy Policy from time to time. Material changes will be communicated
through the Platform, email notification, or the Partner Portal. The “Effective Date” at the top of
this document indicates the date of the most recent revision. Continued use of the Platform
after changes constitutes acceptance of the revised Policy.

15. Grievance Officer & Contact

In accordance with the Information Technology Act, 2000 and the DPDP Act, 2023, the details
of the Grievance Officer are:

   ●   Name: Gopalakrishnan Damodaran

   ●   Designation: Director

   ●   Email: gopal@rizzsoftware.com

●   Phone: (+91) 94453 18886 / (+91) 94453 18887

   ●   Address: Level 8 & 9, Olympia Teknos, No 28, SIDCO Industrial Estate, Guindy, Chennai –
       600032, Tamil Nadu, India.

The Grievance Officer shall acknowledge complaints within 48 hours and endeavour to resolve
them within 30 days.

16. Governing Law

This Privacy Policy shall be governed by and construed in accordance with the laws of India,
including the Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000,
and the rules framed thereunder. Any disputes arising from this Policy shall be subject to the
exclusive jurisdiction of the courts in Chennai, Tamil Nadu, India.
`;

const fixMojibake = (value) => {
  return value
    .replaceAll("â€œ", "“")
    .replaceAll("â€", "”")
    .replaceAll("â€™", "’")
    .replaceAll("â€“", "–")
    .replaceAll("â€”", "—")
    .replaceAll("â€¢", "•")
    .replaceAll("â—", "•");
};

const headingRegex = /^(\d+(?:\.\d+)*)(?:\.)?\s+(.+)$/;
const bulletRegex = /^\s*(?:•|●|\*|-|â—|â€¢)\s+(.+)$/;
const numberRegex = /^\s*(\d+)\.\s+(.+)$/;
const tableSections = new Set(["3.1.1", "3.1.2", "3.1.3", "3.1.5", "3.2", "3.3"]);

const isSpecialLine = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return true;
  if (!/^\s/.test(line) && headingRegex.test(trimmed)) return true;
  if (bulletRegex.test(trimmed)) return true;
  if (numberRegex.test(trimmed) && /^\s+/.test(line)) return true;
  if (/^Data Field\s{2,}Purpose/.test(trimmed)) return true;
  return false;
};

const parseTable = (lines, startIndex) => {
  const headerLine = lines[startIndex].replace(/\s+$/, "");
  const purposeIndex = headerLine.indexOf("Purpose");
  const legalBasisIndex = headerLine.indexOf("Legal Basis");
  if (purposeIndex === -1) return null;

  const hasLegalBasis = legalBasisIndex > -1;
  const headers = hasLegalBasis
    ? ["Data Field", "Purpose", "Legal Basis"]
    : ["Data Field", "Purpose"];

  const rows = [];
  let row = null;
  let i = startIndex + 1;

  while (i < lines.length) {
    const current = lines[i];
    const trimmed = current.trim();
    if (!trimmed) {
      if (row) {
        rows.push(row);
        row = null;
      }
      i += 1;
      if (!lines[i]?.trim()) break;
      continue;
    }

    if (headingRegex.test(trimmed)) break;
    if (bulletRegex.test(trimmed)) break;
    if (/^\d+\.\d+\s/.test(trimmed)) break;
    if (/^\d+\.\s[A-Z]/.test(trimmed)) break;

    const partA = current.slice(0, purposeIndex).trim();
    const partB = hasLegalBasis
      ? current.slice(purposeIndex, legalBasisIndex).trim()
      : current.slice(purposeIndex).trim();
    const partC = hasLegalBasis ? current.slice(legalBasisIndex).trim() : "";

    if (partA) {
      if (row) rows.push(row);
      row = hasLegalBasis ? [partA, partB, partC] : [partA, partB];
    } else if (row) {
      if (partB) row[1] = `${row[1]} ${partB}`.trim();
      if (hasLegalBasis && partC) row[2] = `${row[2]} ${partC}`.trim();
    }

    i += 1;
  }

  if (row) rows.push(row);
  if (!rows.length) return null;
  return { block: { type: "table", headers, rows }, nextIndex: i };
};

const buildPolicyBlocks = (rawText) => {
  const normalized = fixMojibake(rawText);
  const lines = normalized.split("\n").map((line) => line.replace(/\s+$/, ""));
  const blocks = [];
  let currentSection = "";

  for (let i = 0; i < lines.length;) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const headingMatch = !/^\s/.test(line) ? trimmed.match(headingRegex) : null;
    if (headingMatch) {
      const depth = headingMatch[1].split(".").length;
      currentSection = headingMatch[1];
      // Add dot after main heading numbers (1, 2, 3, etc.)
      const dot = depth === 1 ? "." : "";
      blocks.push({
        type: "heading",
        depth,
        text: `${headingMatch[1]}${dot} ${headingMatch[2]}`,
      });
      i += 1;
      continue;
    }

    if (/^Data Field\s{2,}Purpose/.test(trimmed) && tableSections.has(currentSection)) {
      const table = parseTable(lines, i);
      if (table) {
        blocks.push(table.block);
        i = table.nextIndex;
        continue;
      }
    }

    if (bulletRegex.test(trimmed)) {
      const items = [];
      while (i < lines.length && bulletRegex.test(lines[i].trim())) {
        let item = lines[i].trim().replace(bulletRegex, "$1").trim();
        i += 1;
        while (i < lines.length && !isSpecialLine(lines[i])) {
          item += ` ${lines[i].trim()}`;
          i += 1;
        }
        items.push(item);
      }
      blocks.push({ type: "bullets", items });
      continue;
    }

    if (numberRegex.test(trimmed) && /^\s+/.test(line)) {
      const items = [];
      while (i < lines.length && numberRegex.test(lines[i].trim())) {
        let item = lines[i].trim().replace(numberRegex, "$2").trim();
        i += 1;
        while (i < lines.length && !isSpecialLine(lines[i])) {
          item += ` ${lines[i].trim()}`;
          i += 1;
        }
        items.push(item);
      }
      blocks.push({ type: "ordered", items });
      continue;
    }

    let paragraph = trimmed;
    i += 1;
    while (i < lines.length && !isSpecialLine(lines[i])) {
      // Preserve newlines for address blocks
      if (paragraph.includes("Registered Address:")) {
        paragraph += `\n${lines[i].trim()}`;
      } else {
        paragraph += ` ${lines[i].trim()}`;
      }
      i += 1;
    }
    blocks.push({ type: "paragraph", text: paragraph });
  }

  return blocks;
};

const policyBlocks = buildPolicyBlocks(policyText);

const renderParagraphWithFormatting = (text) => {
  // Check if this is an address block
  if (text.includes("Registered Address:")) {
    const lines = text.split("\n").filter(line => line.trim());
    const elements = [];
    lines.forEach((line, i) => {
      const trimmed = line.trim();
      if (trimmed === "Registered Address:") {
        elements.push(<span key={`addr-label-${i}`} className="font-semibold">{trimmed}</span>);
      } else {
        elements.push(<span key={`addr-line-${i}`}>{trimmed}</span>);
      }
      if (i < lines.length - 1) {
        elements.push(<br key={`br-${i}`} />);
      }
    });
    return <>{elements}</>;
  }

  // Original logic for other paragraphs
  const parts = [];
  let lastIndex = 0;

  const regex = /(Effective Date:|Contact:)|(support@rizzsoftware\.com)|(ceviliyar\.in)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.substring(lastIndex, match.index) });
    }

    if (match[1]) {
      parts.push({ type: "label", value: match[1] });
    } else if (match[2]) {
      parts.push({ type: "email", value: match[2] });
    } else if (match[3]) {
      parts.push({ type: "website", value: match[3] });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.substring(lastIndex) });
  }

  return parts.map((part, i) => {
    if (part.type === "label") {
      return <span key={i} className="font-semibold">{part.value}</span>;
    } else if (part.type === "email") {
      return (
        <a key={i} href={`mailto:${part.value}`} className="text-blue-600 hover:underline">
          {part.value}
        </a>
      );
    } else if (part.type === "website") {
      return (
        <a key={i} href={`https://${part.value}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          {part.value}
        </a>
      );
    }
    return <span key={i}>{part.value}</span>;
  });
};

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden overflow-auto h-[100vh]">
      <div className="py-0 md:py-6 lg:py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-10">
            <img
              src="/assests/ceviliyar_mainLogo.png"
              alt="Ceviliyar Logo"
              className="w-[138px] h-[37px]"
            />
          </div>

          <h1 className="text-3xl font-extrabold text-gray-900 mb-8 text-center tracking-tight">
            Privacy Policy
          </h1>

          <div className="max-w-none text-gray-800">
            {policyBlocks.map((block, index) => {
              if (block.type === "heading") {
                const headingClass =
                  block.depth <= 1
                    ? "text-2xl md:text-3xl font-black text-gray-900 mt-10 mb-4"
                    : "text-xl md:text-2xl font-extrabold text-gray-900 mt-8 mb-3";
                return (
                  <h2 key={`heading-${index}`} className={headingClass}>
                    {block.text}
                  </h2>
                );
              }

              if (block.type === "table") {
                return (
                  <div
                    key={`table-${index}`}
                    className="overflow-x-auto border border-gray-200 rounded-xl my-6"
                  >
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          {block.headers.map((header) => (
                            <th
                              key={header}
                              className="text-left px-4 py-3 font-bold text-gray-900 border-b border-gray-200"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((row, rowIndex) => (
                          <tr key={`${row[0]}-${rowIndex}`} className="odd:bg-white even:bg-gray-50">
                            {row.map((cell, cellIndex) => (
                              <td
                                key={`${rowIndex}-${cellIndex}`}
                                className="px-4 py-3 align-top border-b border-gray-100 leading-6"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }

              if (block.type === "bullets") {
                return (
                  <ul key={`bullets-${index}`} className="list-disc pl-7 my-4 space-y-2 marker:text-blue-700">
                    {block.items.map((item, itemIndex) => (
                      <li key={`${item}-${itemIndex}`} className="leading-7 text-[15px]">
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }

              if (block.type === "ordered") {
                return (
                  <ol key={`ordered-${index}`} className="list-decimal pl-7 my-4 space-y-2">
                    {block.items.map((item, itemIndex) => (
                      <li key={`${item}-${itemIndex}`} className="leading-7 text-[15px]">
                        {item}
                      </li>
                    ))}
                  </ol>
                );
              }

              if (block.type === "paragraph") {
                return (
                  <p key={`paragraph-${index}`} className="text-[15px] leading-7 mb-4">
                    {renderParagraphWithFormatting(block.text)}
                  </p>
                );
              }

              return (
                <p key={`paragraph-${index}`} className="text-[15px] leading-7 mb-4">
                  {block.text}
                </p>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;